import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../../../stores/root';
import moment from 'moment';

// assets
import { faCalendarCheck, faCheck, faFilePdf, faLink, faTriangleExclamation, faUpload, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// services
import ErrorService from '../../../../services/general/error';
import ValidationService from '../../../../services/validation';
import GeneralService from '../../../../services/general';
import EmployeeLeaveService from '../../../../services/employees/leave';
import PermissionsService from '../../../../services/permissions';
import UserService from '../../../../services/users/user';

// props
import { IEmployeeLeaveBalanceProps, IEmployeeLeaveResourceProps } from '../../../../props/employees/leaves/leave';

// components
import { DatePicker, DefaultButton, Dropdown, IDropdownOption, Label, Link, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import SelectEmployee from '../../../uiframeworks/forms/selectEmployee';
import SelectSubsidiary from '../../../uiframeworks/forms/selectSubsidiary';
import Text from '../../../typography/text';
import EmployeeLeaveTypes from '../../../../manifests/types/employeeLeave';
import RenderImage from '../../../uiframeworks/files/renderImage';

interface EmployeeLeaveFormProps {
    leaveId?: string;
    defaultEmployeeId?: string;
    onDismissed(refresh?: boolean, status?: string): void;
}

const EmployeeLeaveForm: React.FC<EmployeeLeaveFormProps> = (props: EmployeeLeaveFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [data, setData] = useState<Partial<IEmployeeLeaveResourceProps>>({
        id: GeneralService.guid(),
        employee: user.data || undefined,
        type: 'annual leave'
    });
    const [balance, setBalance] = useState<IEmployeeLeaveBalanceProps | undefined>();
    const [error, setError] = useState<any>({});
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [showUrlInput, setShowUrlInput] = useState<boolean>(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreviewUrl(reader.result as string);
                };
                reader.readAsDataURL(file);
            } else {
                setPreviewUrl(null);
            }
        }
    };

    const canSelectEmployee = PermissionsService.hasPermission(
        ['employees.leaves.read.all', 'employees.leaves.approve.all', 'employees.leaves.confirm.all'],
        user.permissions
    );

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _data = { ...data };

            if (props.leaveId) {
                const leave = await EmployeeLeaveService.read(props.leaveId);
                _data = leave;
                if (leave.attachmentUrl) {
                    setPreviewUrl(leave.attachmentUrl);
                }
            } else if (props.defaultEmployeeId) {
                try {
                    const emp = await UserService.get(props.defaultEmployeeId);
                    _data.employee = emp;
                } catch (e) {
                    console.error("Failed to load default employee", e);
                }
            }

            const targetEmpId = _data.employee?.id || user.data?.id;
            if (targetEmpId) {
                const bal = await EmployeeLeaveService.getBalance(`employeeId=${targetEmpId}&year=${moment().year()}`);
                setBalance(bal);
            }

            setData(_data);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_leave_init_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to load leave form. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    };

    // Calculate business days (Saturdays & Sundays excluded)
    const calculatedWorkDays = useMemo(() => {
        if (!data.startDate || !data.endDate) return 0;
        const start = moment(data.startDate).startOf('day');
        const end = moment(data.endDate).startOf('day');
        if (end.isBefore(start)) return 0;

        let count = 0;
        const curr = start.clone();
        while (curr.isSameOrBefore(end, 'day')) {
            const dayOfWeek = curr.day();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) { // 0 = Sunday, 6 = Saturday
                count++;
            }
            curr.add(1, 'day');
        }
        return count;
    }, [data.startDate, data.endDate]);

    // Validate remaining quota
    const quotaValidation = useMemo(() => {
        if (!balance || calculatedWorkDays <= 0) return { isValid: true, message: '' };

        const type = (data.type || '').toLowerCase();
        const isAnnual = type === 'annual leave' || type === 'annual' || type.includes('tahunan');
        const isWfh = type === 'wfh' || type.includes('home');

        if (isAnnual) {
            if (calculatedWorkDays > balance.annualRemaining) {
                return {
                    isValid: false,
                    message: `Request (${calculatedWorkDays} days) exceeds applicant's remaining Annual Leave (${balance.annualRemaining} days remaining).`
                };
            }
        } else if (isWfh) {
            if (calculatedWorkDays > balance.wfhRemaining) {
                return {
                    isValid: false,
                    message: `Request (${calculatedWorkDays} days) exceeds applicant's remaining WFH quota (${balance.wfhRemaining} days remaining).`
                };
            }
        }

        return { isValid: true, message: '' };
    }, [balance, calculatedWorkDays, data.type]);

    const isSubmitButtonDisabled = (): boolean => {
        if (!data.subsidiary) return true;
        if (!data.reason || data.reason.trim() === '') return true;
        if (!data.startDate || !data.endDate || !data.type) return true;
        if (calculatedWorkDays <= 0) return true;
        if (!quotaValidation.isValid) return true;
        if (error.subsidiary || error.reason) return true;

        return false;
    };

    const _onSubmit = async () => {
        try {
            const confirmed = window.confirm(`Submit this leave request for ${calculatedWorkDays} working day(s)?`);
            if (confirmed) {
                setSubmitting(true);
                const { employee, subsidiary, startDate, endDate, type, reason, attachmentUrl } = data;

                const fd = new FormData();
                fd.append('employeeId', employee?.id || user.data?.id || '');
                fd.append('subsidiaryId', subsidiary?.id || '');
                fd.append('startDate', moment(startDate).toISOString());
                fd.append('endDate', moment(endDate).toISOString());
                fd.append('totalDays', String(calculatedWorkDays));
                fd.append('type', type || 'annual leave');
                fd.append('reason', reason || '');
                if (selectedFile) {
                    fd.append('file', selectedFile);
                    fd.append('attachment', selectedFile);
                    fd.append('image', selectedFile);
                } else if (attachmentUrl) {
                    fd.append('attachmentUrl', attachmentUrl);
                }

                if (props.leaveId) {
                    await EmployeeLeaveService.update(props.leaveId, fd);
                } else {
                    await EmployeeLeaveService.create(fd);
                }

                banner.add({
                    key: 'request_employee_leave_success',
                    variant: 'success',
                    icon: faCheck,
                    text: `Leave request submitted successfully and is pending review.`
                });
                props.onDismissed(true, 'requested');
            }
        } catch (e) {
            banner.add({
                key: 'request_leave_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to submit leave request. Error: ${ErrorService.getMessage(e)}`
            });
            setSubmitting(false);
        }
    };

    return (
        <Panel
            headerText={props.leaveId ? 'Edit Leave Request' : 'Request Leave & WFH'}
            isOpen={true}
            type={PanelType.medium}
            onDismiss={() => props.onDismissed(false)}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {!submitting ? (
                        <>
                            <PrimaryButton
                                text={props.leaveId ? 'Save Changes' : 'Submit Request'}
                                disabled={isSubmitButtonDisabled()}
                                onClick={_onSubmit}
                            />
                            <DefaultButton text="Cancel" onClick={() => props.onDismissed(false)} />
                        </>
                    ) : (
                        <Spinner size={SpinnerSize.medium} labelPosition="right" label="Submitting request..." />
                    )}
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { paddingTop: 10 } }}>
                {!loaded ? (
                    <Spinner size={SpinnerSize.medium} labelPosition="right" label="Loading form & balance..." />
                ) : (
                    <>
                        {/* Live Balance Card */}
                        {balance && (
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: '12px 16px',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 12,
                                textAlign: 'center'
                            }}>
                                <div style={{ borderRight: '1px solid #e2e8f0', paddingRight: 8 }}>
                                    <Text size="xsmall" style={{ color: '#64748b', fontWeight: 600 }}>Remaining Annual Leave</Text>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0284c7', marginTop: 2 }}>
                                        {balance.annualRemaining} <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>/ {balance.annualQuota} days</span>
                                    </div>
                                    <Text size="xsmall" style={{ color: '#94a3b8' }}>Used: {balance.annualUsed} days</Text>
                                </div>
                                <div style={{ borderRight: '1px solid #e2e8f0', paddingRight: 8 }}>
                                    <Text size="xsmall" style={{ color: '#64748b', fontWeight: 600 }}>Remaining WFH</Text>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#10b981', marginTop: 2 }}>
                                        {balance.wfhRemaining} <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>/ {balance.wfhQuota} days</span>
                                    </div>
                                    <Text size="xsmall" style={{ color: '#94a3b8' }}>Used: {balance.wfhUsed} days</Text>
                                </div>
                                <div>
                                    <Text size="xsmall" style={{ color: '#64748b', fontWeight: 600 }}>Special Leave</Text>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#8b5cf6', marginTop: 2 }}>
                                        {balance.specialUsed} <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>days</span>
                                    </div>
                                    <Text size="xsmall" style={{ color: '#94a3b8' }}>No Quota Deduction</Text>
                                </div>
                            </div>
                        )}

                        <SelectEmployee
                            selected={data.employee}
                            disabled={!canSelectEmployee || submitting}
                            label="Applicant (Employee)"
                            onChange={async (emp) => {
                                if (emp) {
                                    setData({ ...data, employee: emp });
                                    try {
                                        const bal = await EmployeeLeaveService.getBalance(`employeeId=${emp.id}&year=${moment(data.startDate || undefined).year()}`);
                                        setBalance(bal);
                                    } catch (e) { }
                                }
                            }}
                        />

                        <SelectSubsidiary
                            selected={data.subsidiary}
                            label="Subsidiary / Entity"
                            required
                            onChange={(value) => {
                                const _data = { ...data, subsidiary: value };
                                const validation = ValidationService.combination(value, ['required'], {});
                                error.subsidiary = validation.message;
                                setData(_data);
                                setError({ ...error });
                            }}
                        />

                        <Dropdown
                            label="Leave / Facility Type"
                            options={EmployeeLeaveTypes}
                            selectedKey={data.type}
                            required
                            onChange={(evt: any, opt?: IDropdownOption) => {
                                if (opt) {
                                    setData({ ...data, type: opt.key as string });
                                }
                            }}
                        />

                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <DatePicker
                                label="Start Date"
                                isRequired={true}
                                value={data.startDate ? moment(data.startDate).toDate() : undefined}
                                onSelectDate={(date) => {
                                    setData({
                                        ...data,
                                        startDate: date ? moment(date).toISOString() : data.startDate
                                    });
                                }}
                                formatDate={GeneralService.formatDate}
                                disabled={submitting}
                                styles={{ root: { width: '50%' } }}
                            />
                            <DatePicker
                                label="End Date"
                                isRequired={true}
                                minDate={data.startDate ? moment(data.startDate).toDate() : undefined}
                                value={data.endDate ? moment(data.endDate).toDate() : undefined}
                                onSelectDate={(date) => {
                                    setData({
                                        ...data,
                                        endDate: date ? moment(date).toISOString() : data.endDate
                                    });
                                }}
                                formatDate={GeneralService.formatDate}
                                disabled={submitting}
                                styles={{ root: { width: '50%' } }}
                            />
                        </Stack>

                        {/* Calculated Work Days Banner */}
                        {data.startDate && data.endDate && (
                            <div style={{
                                background: calculatedWorkDays > 0 ? '#eff6ff' : '#fef2f2',
                                border: `1px solid ${calculatedWorkDays > 0 ? '#bfdbfe' : '#fecaca'}`,
                                borderRadius: 6,
                                padding: '8px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                    <FontAwesomeIcon
                                        icon={calculatedWorkDays > 0 ? faCalendarCheck : faTriangleExclamation}
                                        style={{ color: calculatedWorkDays > 0 ? '#2563eb' : '#dc2626' }}
                                    />
                                    <div>
                                        <Text size="small" style={{ fontWeight: 600 }}>
                                            Duration: {calculatedWorkDays} Working Day(s)
                                        </Text>
                                        <Text size="xsmall" style={{ color: '#64748b', display: 'block' }}>
                                            (Saturdays and Sundays are automatically excluded from leave count)
                                        </Text>
                                    </div>
                                </Stack>
                            </div>
                        )}

                        {/* Quota Exceeded Warning */}
                        {!quotaValidation.isValid && (
                            <MessageBar messageBarType={MessageBarType.error}>
                                {quotaValidation.message}
                            </MessageBar>
                        )}

                        <TextField
                            label="Reason / Remarks"
                            value={data.reason}
                            multiline
                            rows={3}
                            autoAdjustHeight
                            resizable={false}
                            required
                            placeholder="Example: Urgent personal family matter / Medical recovery"
                            onChange={(evt: any, value?: string) => {
                                const _data = { ...data, reason: value };
                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 2000 });
                                error.reason = validation.message;
                                setData(_data);
                                setError({ ...error });
                            }}
                        />

                        <div style={{ marginTop: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <Label style={{ fontSize: 13, fontWeight: 600, color: '#323130' }}>
                                    Supporting Document / Medical Certificate (SKD)
                                </Label>
                                <span style={{ fontSize: 11, color: '#64748b' }}>(Optional)</span>
                            </div>
                            <Text size="xsmall" style={{ color: '#64748b', display: 'block', marginBottom: 8 }}>
                                Upload foto surat dokter (SKD) untuk cuti sakit, atau dokumen pendukung lainnya (JPG, PNG, PDF maks. 5MB).
                            </Text>

                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*,application/pdf"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />

                            {/* Case 1: A file is newly selected */}
                            {selectedFile ? (
                                <div style={{
                                    border: '1px solid #cbd5e1',
                                    borderRadius: 8,
                                    padding: '12px 16px',
                                    background: '#f8fafc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflow: 'hidden' }}>
                                        {previewUrl ? (
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                style={{
                                                    width: 54,
                                                    height: 54,
                                                    objectFit: 'cover',
                                                    borderRadius: 6,
                                                    border: '1px solid #e2e8f0'
                                                }}
                                            />
                                        ) : (
                                            <div style={{
                                                width: 54,
                                                height: 54,
                                                borderRadius: 6,
                                                background: '#fee2e2',
                                                color: '#ef4444',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 22
                                            }}>
                                                <FontAwesomeIcon icon={faFilePdf} />
                                            </div>
                                        )}
                                        <div style={{ overflow: 'hidden' }}>
                                            <Text size="small" style={{ fontWeight: 600, display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                {selectedFile.name}
                                            </Text>
                                            <Text size="xsmall" style={{ color: '#64748b' }}>
                                                {GeneralService.getFriendlyFileSizeText(selectedFile.size)}
                                            </Text>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                        <DefaultButton
                                            text="Ganti"
                                            iconProps={{ iconName: 'Edit' }}
                                            onClick={() => fileInputRef.current?.click()}
                                        />
                                        <DefaultButton
                                            text="Hapus"
                                            iconProps={{ iconName: 'Delete' }}
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setPreviewUrl(null);
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : data.attachmentUrl && !showUrlInput ? (
                                /* Case 2: Existing attachment already on server */
                                <div style={{
                                    border: '1px solid #cbd5e1',
                                    borderRadius: 8,
                                    padding: '12px 16px',
                                    background: '#f8fafc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflow: 'hidden' }}>
                                        {data.attachmentUrl.startsWith('http') || data.attachmentUrl.startsWith('data:') ? (
                                            <img
                                                src={data.attachmentUrl}
                                                alt="Current attachment"
                                                style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
                                                onError={(e) => (e.currentTarget.style.display = 'none')}
                                            />
                                        ) : (
                                            <div style={{ width: 54, height: 54, borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                                <RenderImage path={data.attachmentUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                        <div style={{ overflow: 'hidden' }}>
                                            <Text size="small" style={{ fontWeight: 600, display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                Dokumen Terlampir
                                            </Text>
                                            <Text size="xsmall" style={{ color: '#0284c7' }}>
                                                {data.attachmentUrl.startsWith('http') ? (
                                                    <a href={data.attachmentUrl} target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'underline' }}>
                                                        Buka File
                                                    </a>
                                                ) : 'Tersimpan di Cloud'}
                                            </Text>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                        <DefaultButton
                                            text="Ganti File"
                                            iconProps={{ iconName: 'Edit' }}
                                            onClick={() => fileInputRef.current?.click()}
                                        />
                                        <DefaultButton
                                            text="Hapus"
                                            iconProps={{ iconName: 'Delete' }}
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setPreviewUrl(null);
                                                setData({ ...data, attachmentUrl: '' });
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : !showUrlInput ? (
                                /* Case 3: Empty upload box */
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{
                                        border: '2px dashed #cbd5e1',
                                        borderRadius: 8,
                                        padding: '24px 16px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        background: '#fafafa',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#0284c7')}
                                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#cbd5e1')}
                                >
                                    <div style={{ color: '#0284c7', fontSize: 26, marginBottom: 8 }}>
                                        <FontAwesomeIcon icon={faUpload} />
                                    </div>
                                    <Text size="small" style={{ fontWeight: 600, color: '#334155', display: 'block' }}>
                                        Klik untuk upload gambar atau dokumen (SKD)
                                    </Text>
                                    <Text size="xsmall" style={{ color: '#94a3b8', display: 'block', marginTop: 4 }}>
                                        Mendukung format PNG, JPG, JPEG, WEBP, atau PDF
                                    </Text>
                                    <div style={{ marginTop: 12 }}>
                                        <DefaultButton
                                            text="Pilih File dari Komputer"
                                            iconProps={{ iconName: 'Attach' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                fileInputRef.current?.click();
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                                <Link
                                    style={{ fontSize: 11, color: '#64748b' }}
                                    onClick={() => setShowUrlInput(!showUrlInput)}
                                >
                                    {showUrlInput ? '« Kembali ke upload file/gambar' : 'Atau input tautan URL dokumen (Google Drive) »'}
                                </Link>
                            </div>

                            {showUrlInput && (
                                <div style={{ marginTop: 8 }}>
                                    <TextField
                                        label="Tautan URL Dokumen (Google Drive / Cloud Link)"
                                        value={data.attachmentUrl || ''}
                                        placeholder="https://... atau link dokumen pendukung"
                                        onChange={(evt: any, value?: string) => {
                                            setData({ ...data, attachmentUrl: value || '' });
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Stack>
        </Panel>
    );
};

export default EmployeeLeaveForm;
